import axios from 'axios';
import { API_BASE_URL } from '@config';

export const LOGIN_TRAINING_REQUEST = '[TRAINING] LOGIN REQUEST';
export const LOGIN_TRAINING_SUCCESS = '[TRAINING] LOGIN SUCCESS';
export const LOGIN_TRAINING_FAILURE = '[TRAINING] LOGIN FAILURE';

export const VERIFY_CODE_REQUEST = '[TRAINING] VERIFY CODE REQUEST';
export const VERIFY_CODE_SUCCESS = '[TRAINING] VERIFY CODE SUCCESS';
export const VERIFY_CODE_FAILURE = '[TRAINING] VERIFY CODE FAILURE';

export const CHECK_PARTICIPANT_REQUEST = '[TRAINING] CHECK PARTICIPANT REQUEST';
export const CHECK_PARTICIPANT_SUCCESS = '[TRAINING] CHECK PARTICIPANT SUCCESS';
export const CHECK_PARTICIPANT_FAILURE = '[TRAINING] CHECK PARTICIPANT FAILURE';

export function checkTrainingParticipant({ email, sessionCode }) {
  return async (dispatch) => {
    dispatch({ type: CHECK_PARTICIPANT_REQUEST });
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/training/check-participant`, {
        email,
        sessionCode,
      });
      dispatch({ type: CHECK_PARTICIPANT_SUCCESS, payload: response.data });
      return response.data;
    } catch (err) {
      const errorMessage = err && err.data && err.data.error ? err.data.error : 'An unknown error has occurred.';
      dispatch({ type: CHECK_PARTICIPANT_FAILURE, payload: errorMessage });

      const customError = new Error(errorMessage);
      customError.response = err;
      customError.responseData = errorMessage;
      customError.allowedDomains = err.data?.allowedDomains;
      throw customError;
    }
  };
}

/**
 * Step one of the participant login: ask whether two-step verification is
 * required (and have the code sent if so). This deliberately does NOT call
 * /api/auth/training/register - the caller does that exactly once via
 * submitLoginForTrainingSession (which also installs the JWT). Registering
 * here as well used to create two server sessions per login, doubling every
 * participant's login count on the Heidi User Activity page.
 */
export function loginTraining({ fullName, email, sessionCode }) {
  return async (dispatch) => {
    dispatch({ type: LOGIN_TRAINING_REQUEST });
    try {
      const verificationRes = await axios.post(`${API_BASE_URL}/api/two-step-verification/generate-code`, {
        fullName,
        email,
        sessionCode,
      });

      if (verificationRes.data.verificationRequired) {
        dispatch({ type: VERIFY_CODE_SUCCESS, payload: { verificationRequired: true, message: verificationRes.data.message } });
        return { verificationRequired: true };
      }
      dispatch({ type: LOGIN_TRAINING_SUCCESS, payload: { verificationRequired: false } });
      return { verificationRequired: false };
    } catch (err) {
      const errorMessage = err && err.data && err.data.error ? err.data.error : "An unknown error has occurred."
      dispatch({ type: LOGIN_TRAINING_FAILURE, payload: errorMessage });

      const customError = new Error(errorMessage);
      customError.response = err;
      customError.responseData = errorMessage;
      
      // Add license capacity error handling
      if (err.data?.reason === 'LicenseCapacityReached') {
        customError.licenseError = true;
        customError.message = "License capacity reached. Cannot create new session.";
      }
      
      customError.allowedDomains = err.data.allowedDomains;

      throw customError;
    }
  };
}

export function verifyTrainingCode({ email, sessionCode, code }) {
  return (dispatch) => {
    dispatch({ type: VERIFY_CODE_REQUEST });

    return new Promise((resolve) => {
      axios.post(
        `${API_BASE_URL}/api/two-step-verification/verify-code`,
        { email, code, sessionCode },
        { validateStatus: () => true } // Always resolve, never throw
      )
        .then(response => {
          if (response.status >= 200 && response.status < 300 && response.data.verified) {
            // On success, just resolve the promise. The component will handle the next steps.
            resolve({ success: true, data: response.data });
          } else {
            // If the status code is not 2xx or verification is false, handle as an error.
            const errorMessage = response.data?.error || 'Invalid or expired verification code';
            dispatch({ type: VERIFY_CODE_FAILURE, payload: errorMessage });
            resolve({ success: false, error: errorMessage });
          }
        })
        .catch(error => {
          // This will now only catch network errors or other unexpected issues.
          const errorMessage = 'An unknown error has occurred.';
          dispatch({ type: VERIFY_CODE_FAILURE, payload: errorMessage });
          resolve({ success: false, error: errorMessage });
        });
    });
  };
}

/**
 * Terminates active VNC session for a single user (used for cluster-app launches)
 */
export async function terminateUserVncSession(username, clusterId) {
  try {
      const response = await axios.post(`${API_BASE_URL}/api/vnc/terminate-session`, {
          username: username,
          clusterId: clusterId
      });

      return { success: true, message: response.data.message };
  } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to terminate VNC session';
      return { success: false, error: errorMessage };
  }
}

/**
 * Terminates active VNC sessions for users but does NOT delete their system directories.
 */
export async function terminateUsersVncSession({ trainingId, userEmails, clusterId }) {
  try {
      const response = await axios.post(`${API_BASE_URL}/api/trainings/${trainingId}/users/terminate-vnc-session`, { userEmails, clusterId });

      return { success: true, message: response.data.message };
  } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to terminate VNC sessions';
      return { success: false, error: errorMessage };
  }
}


/**
* Completely removes users from the training session, including deleting their system directories.
*/
export async function removeUsersCompletely({ trainingId, userEmails, clusterId }) {
  try {
      const response = await axios.post(`${API_BASE_URL}/api/trainings/${trainingId}/users/remove-completely`, { userEmails, clusterId });

      return { success: true, message: response.data.message };
  } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to remove users completely';
      return { success: false, error: errorMessage };
  }
}
